'use client'
import { useSearchType } from '@/api/hooks'
import { SectionHeader } from '@/components/sections'
import Toggle from '@/components/toggle'
import { SearchTypeContext } from '@/context/search-type'
import { setGADataLayer } from 'analytics'
import { useContext } from 'react'
import styled, { css } from 'styled-components'
import { desktop } from 'ui/src/css'
import TrendingIcon from '../icons/trending'
import { DeviceType } from '../util/env'
import type { HomeHeaderCopy } from './chrome-copy'
import { mobileSearchTypes, searchTypes } from './search/types'

const GridHeader = styled.div`
    ${desktop(css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    `)}
`

type Props = {
    children?: React.ReactNode
    deviceType: DeviceType
    /** Resolved on the server; see app/(site)/chrome-copy.ts. */
    copy: HomeHeaderCopy
}

const HomeHeader = ({ children, deviceType, copy }: Props) => {
    const { setTransitioning } = useContext(SearchTypeContext)
    const searchType = useSearchType()
    const isMobile = deviceType === 'mobile'

    return (
        <div className="mt-1 mb-2.5 flex flex-col gap-1.5 md:my-2.5 md:flex-col-reverse md:gap-2.5">
            <GridHeader>
                <SectionHeader
                    Icon={<TrendingIcon className="size-5" />}
                    label={copy.home.trendingNow}
                    tag="h1"
                    className="hidden md:inline-block"
                />
                <Toggle
                    isMobile={isMobile}
                    className="w-full"
                    index={Math.max((isMobile ? mobileSearchTypes : searchTypes).indexOf(searchType || 'gifs'), 0)}
                    onClick={(index) => {
                        const nextType = (isMobile ? mobileSearchTypes : searchTypes)[index]
                        setTransitioning(nextType !== searchType)
                        setGADataLayer({
                            event: 'page_view',
                            options: {
                                pageType: 'home',
                                pageCategory: `homepage_${nextType}`,
                            },
                        })
                    }}
                    options={
                        isMobile
                            ? // Deliberately still English. Toggle sizes these buttons at a
                              // fixed 88px and positions the highlight pill at a hardcoded
                              // 96px pitch, so the two constants have to agree. That leaves
                              // 64px of text room, and "Tendencias" and "Reacciones" need
                              // roughly 90px -- they would overflow the button and the pill
                              // would stop covering the label. Translating these is blocked
                              // on making that toggle size to its content; the Spanish
                              // strings are already in the catalog under home_toggle_*.
                              [
                                  { label: 'Trending', href: '/' },
                                  { label: 'Stickers', href: '/?type=stickers' },
                                  { label: 'Emoji', href: '/?type=emoji' },
                                  { label: 'Memes', href: '/?type=memes' },
                                  { label: 'Reactions', href: '/?type=reactions' },
                                  { label: 'Animals', href: '/?type=animals' },
                                  { label: 'Text', href: '/?type=text' },
                              ]
                            : // Desktop sizes each button at 1/n of the row, so these are
                              // free to translate -- and the format names read the same in
                              // both languages anyway.
                              [
                                  { label: copy.formats.gifs, href: '/' },
                                  { label: copy.formats.stickers, href: '/?type=stickers' },
                                  { label: copy.formats.clips, href: '/?type=clips' },
                              ]
                    }
                />
            </GridHeader>
            {children}
        </div>
    )
}

export default HomeHeader
